import { FormControl, Text, FormLabel, useOutsideClick, HStack, Tooltip } from '@chakra-ui/react';
import { forwardRef, ForwardRefRenderFunction, useMemo, useRef, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import { FieldError } from 'react-hook-form';
import { ActionMeta, OnChangeValue, PropsValue, SelectInstance, StylesConfig } from 'react-select';
import { BsFillInfoCircleFill } from 'react-icons/bs';

type SelectStyle = {
  showBorder?: boolean;
  error?: boolean;
  colorScheme?: string;
};

function selectStyle({ error, showBorder }: SelectStyle): StylesConfig<ISelectOptions, false> {
  return {
    input: base => ({
      ...base,
      width: '100%',
    }),
    control: (base, props) => {
      let borderColor = showBorder ? '#D1D1D1 !important' : 'transparent';

      if (props.isFocused) {
        borderColor = `#007AFF !important`;
      } else if (error) {
        borderColor = '#E53E3E !important';
      }

      return {
        ...base,
        width: '100%',
        boxShadow: '0',
        height: 'var(--chakra-sizes-10)',
        fontSize: 'var(--chakra-fontSizes-md)',
        border: '2px solid transparent',
        borderColor,
        backgroundColor: 'transparent',
        borderRadius: 'var(--chakra-radii-md)',
        ':hover': {
          borderColor: props.isFocused ? `#7B7B7B` : 'transparent',
        },
      };
    },
    option: (base, props) => ({
      ...base,
      color: props.isFocused ? 'var(--chakra-colors-gray-50)' : 'inherit',
      background: props.isFocused ? `#7B7B7B` : 'white',
      ':hover': {
        background: `#7B7B7B`,
        color: 'var(--chakra-colors-gray-50)',
      },
    }),
    menu: provided => ({
      ...provided,
      overflow: 'hidden',
    }),
    menuList: provided => ({
      ...provided,
      padding: 0,
    }),
  };
}

interface ISelectProps<Option = ISelectOptions> {
  name: string;
  colorScheme: string;
  label?: string;
  options: Option[];
  value?: PropsValue<ISelectOptions> | undefined;
  error?: FieldError | string | null;
  showBorder?: boolean;
  isClearable?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  onChange?: (newValue: OnChangeValue<Option, false>, actionMeta: ActionMeta<Option>) => void;
  onInputChange?: (inputValue, actionMeta) => void;
  viewTooltip?: boolean;
  tooltipLabel?: string;
}

export interface ISelectOptions {
  label: string;
  value: string;
  detalhes?: object;
  descricaoMinima?: string;
}

const AutoCompleteInputBase: ForwardRefRenderFunction<SelectInstance<ISelectOptions>, ISelectProps> = (
  { name, label, error, options, value, onChange, autoFocus, placeholder, isClearable, showBorder, colorScheme, onInputChange, viewTooltip, tooltipLabel, ...rest },
  ref,
): JSX.Element => {
  const selectRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  useOutsideClick({
    ref: selectRef,
    handler: () => setIsOpen(false),
  });

  const errorMessage = useMemo(() => {
    if (error) {
      if (typeof error === 'string') {
        return error;
      }
      const fieldError = error as FieldError;
      return fieldError.message;
    }
    return '';
  }, [error]);

  return (
    <FormControl ref={selectRef}>
      {!!label && (
        <FormLabel
          htmlFor={name}
          mb={0.5}
          onClick={() => {
            setIsOpen(true);
            const reference = ref as { current?: { focus: () => void } };
            reference.current?.focus();
          }}
        >
          <HStack w="100%">
            <Text fontWeight="bold" whiteSpace="nowrap" fontSize="xs">
              {label}
            </Text>
            {!!viewTooltip && (
              <Tooltip label={tooltipLabel} bg="gray.100" color="black" placement="top-start">
                <span>
                  <BsFillInfoCircleFill size={16} color="#ffb818" />
                </span>
              </Tooltip>
            )}
            {!!error && (
              <Text fontSize="xs" color="red.500" whiteSpace="nowrap">
                {errorMessage}
              </Text>
            )}
          </HStack>
        </FormLabel>
      )}
      <CreatableSelect
        ref={ref}
        options={options}
        autoFocus={autoFocus}
        menuIsOpen={isOpen ? true : undefined}
        closeMenuOnSelect
        styles={selectStyle({ error: !!error, showBorder, colorScheme })}
        onChange={onChange}
        isClearable={isClearable}
        value={value}
        placeholder={placeholder || 'Escolha ou Cadastre...'}
        noOptionsMessage={() => 'Sem resultados...'}
        onInputChange={(inputValue, actionMeta) => {
          setIsOpen(!!inputValue);
          onInputChange?.(inputValue, actionMeta);
        }}
        createOptionPosition="first"
        formatCreateLabel={(inputValue: string) => `Adicionar "${inputValue}"`}
        {...rest}
      />
      {!!errorMessage && (
        <Text fontSize="sm" color="red" my={1}>
          {errorMessage}
        </Text>
      )}
    </FormControl>
  );
};

const AutoComplete = forwardRef(AutoCompleteInputBase);

export { AutoComplete };
